import { Switch, type SwitchProps, FormControlLabel, type FormControlLabelProps } from '@mui/material';
import { type Control, Controller, type FieldValues, type Path, type RegisterOptions } from 'react-hook-form';

type FormSwitchFieldProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  label: React.ReactNode;
  checkedValue?: string | number | boolean;
  uncheckedValue?: string | number | boolean;
  onValueChange?: (checked: boolean) => void;
  slotProps?: { formControlLabel?: Partial<FormControlLabelProps> };
} & Omit<SwitchProps, 'name' | 'defaultValue' | 'value' | 'onChange' | 'inputRef' | 'checked'>;

export function FormSwitchField<TFieldValues extends FieldValues>(props: FormSwitchFieldProps<TFieldValues>) {
  const {
    name,
    control,
    rules,
    label,
    checkedValue = true,
    uncheckedValue = false,
    onValueChange,
    slotProps,
    ...switchProps
  } = props;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const isChecked = field.value === checkedValue;

        return (
          <FormControlLabel
            label={label}
            {...slotProps?.formControlLabel}
            control={
              <Switch
                {...field}
                {...switchProps}
                checked={isChecked}
                onChange={(e) => {
                  field.onChange(e.target.checked ? checkedValue : uncheckedValue);
                  onValueChange?.(e.target.checked);
                }}
              />
            }
          />
        );
      }}
    />
  );
}
